import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { getFiberSiteAuditLogs } from './functions/get_fiber_site_audit_logs.function';

export default function FiberSiteHistoryPanel({ siteCode, onAuditLogClick, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(null);

  const hasFetchedLogs = useRef(false); // to track if we already fetched logs

  useEffect(() => {
    if (siteCode && !hasFetchedLogs.current) {
      hasFetchedLogs.current = true;
      setLoading(true);
      const fetchLogs = async () => {
        try {
          const data = await getFiberSiteAuditLogs(siteCode);
          const sortedData = data.sort((a, b) => {
            const dateA = new Date(a.created_at || a.timestamp || 0);
            const dateB = new Date(b.created_at || b.timestamp || 0);
            return dateB - dateA;
          });
          setLogs(sortedData);
        } catch (error) {
          console.error('Error fetching fiber site audit logs:', error);
          setLogs([]);
        } finally {
          setLoading(false);
        }
      };
      fetchLogs();
    }
  }, [siteCode]);

  // Clear state when component unmounts or onClose is called
  useEffect(() => {
    return () => {
      setLogs([]);
      setSelectedLogId(null);
      setSelectedSiteIndex(null);
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  const handleSiteClick = (log, siteIndex) => {
    console.log('Fiber Site clicked - Log ID:', log.id, 'Site Index:', siteIndex);
    setSelectedLogId(log.id);
    setSelectedSiteIndex(siteIndex);
    if (log.details && log.details.updatedSites && log.details.updatedSites[siteIndex]) {
      onAuditLogClick && onAuditLogClick(log, siteIndex);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        minWidth: 250,
        height: '100vh',
        bgcolor: '#fafbfc',
        borderRadius: 2,
        boxShadow: 1,
        p: 2,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <HistoryIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">History</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={32} />
        </Box>
      ) : logs.length === 0 ? (
        <Typography color="text.secondary">No audit logs found.</Typography>
      ) : (
        <List dense sx={{ overflowY: 'auto', flex: 1 }}>
          {logs
            .filter(
              (log) =>
                log.details && log.details.updatedSites && log.details.updatedSites.length > 0
            )
            .map((log) => {
              console.log(
                'Rendering fiber site log:',
                log.id,
                'Selected:',
                selectedLogId === log.id,
                'Site Index:',
                selectedSiteIndex
              );
              return (
                <ListItem
                  key={log.id}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    boxShadow: 1,
                    p: 2,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ width: '100%', mb: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ fontSize: '12px', mb: 0.5 }}
                    >
                      {log.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                      {formatDate(log.created_at || log.timestamp)} -{' '}
                      {formatTime(log.created_at || log.timestamp)}
                    </Typography>
                  </Box>

                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        fontSize: '11px',
                        fontWeight: 600,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      {/* Updated Sites ({log.details.updatedSites.length}): */}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {log.details.updatedSites.map((site, index) => (
                        <Box
                          key={index}
                          onClick={() => handleSiteClick(log, index)}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor:
                              selectedLogId === log.id && selectedSiteIndex === index
                                ? '#1976d2'
                                : '#ddd',
                            backgroundColor:
                              selectedLogId === log.id && selectedSiteIndex === index
                                ? '#1976d2'
                                : '#fff',
                            color:
                              selectedLogId === log.id && selectedSiteIndex === index
                                ? '#fff'
                                : '#333',
                            fontWeight: 600,
                            fontSize: '14px',
                            '&:hover': {
                              borderColor: '#1976d2',
                              backgroundColor:
                                selectedLogId === log.id && selectedSiteIndex === index
                                  ? '#1976d2'
                                  : '#f5f5f5',
                            },
                          }}
                        >
                          {index + 1}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
        </List>
      )}
    </Box>
  );
}
